/**
 * One session row — a clean rewrite of the legacy SessionListItem +
 * SidebarSessionContextMenu pair, minus the branch subtitle / inclusion dot /
 * drag source. Name, an optional working indicator, and a lightweight hover menu
 * (rename inline, delete with an inline confirm — no modal, no window.confirm).
 */

import { isSessionDone, type SessionRunStatus } from '@mod/core/model'
import { Check, MoreHorizontal, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { SessionMark } from '../../services/sessions/SessionMark'
import { ActivityGrid } from '../../shell/window/ActivityGrid'
import { StatusDot, sessionDot } from '../../shell/window/StatusDot'

export interface SessionRowData {
  id: string
  title: string
  updatedAt?: number
  /** When it was filed as done, by a person or an agent (RFC 0286). Absent means not done. */
  completedAt?: number
  /** When `status` was claimed; with `updatedAt`, what says a filed
   *  session has started again (isSessionDone). */
  statusAt?: number
  /** When a message was last sent to it (isSessionDone reads it). */
  sentAt?: number
  /** What it is doing (RFC 0286), from mod-core's projection. Absent means the
   *  row was not read, which is UNKNOWN and deliberately not 'idle'. */
  status?: SessionRunStatus
  /**
   * The project that holds this session (RFC 0290 S5). Set only in the INBOX,
   * where rows come from more than one project and "which project is this" is
   * the question the list cannot otherwise answer. A single project's list
   * omits it rather than repeating one name down every row.
   */
  projectName?: string
  /**
   * The session that opened this one, when an agent did (`start_session`).
   *
   * Absent means a person started it. The list uses it to decide whether the
   * row belongs at the top level at all; the row itself does not draw it,
   * because a sub-session shown IS shown as a peer and a subtitle saying "this
   * is really somebody else's" would be a hierarchy drawn in words.
   */
  parentSessionId?: string
}

export function SessionRow({
  session,
  isActive,
  onOpen,
  onRename,
  onDelete,
  onComplete,
}: {
  session: SessionRowData
  isActive: boolean
  onOpen: () => void
  onRename?: (sessionId: string, title: string) => void
  onDelete?: (sessionId: string) => void
  onComplete?: (sessionId: string, done: boolean) => void
}) {
  // The same dot the document header draws for this session (RFC 0311 3e).
  const done = isSessionDone(session)
  const rowDot = sessionDot(
    session.status,
    done ? session.completedAt : undefined,
  )
  const [menuOpen, setMenuOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [draft, setDraft] = useState(session.title)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (renaming) inputRef.current?.select()
  }, [renaming])

  const commitRename = () => {
    const next = draft.trim()
    if (next && next !== session.title) onRename?.(session.id, next)
    setRenaming(false)
  }

  if (renaming) {
    return (
      <div className="px-2">
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename()
            if (e.key === 'Escape') {
              setDraft(session.title)
              setRenaming(false)
            }
          }}
          // biome-ignore lint/a11y/noAutofocus: focus follows an explicit rename action
          autoFocus
          className="w-full rounded border border-border bg-background px-2 py-1 text-sm outline-none focus:border-blue-500"
        />
      </div>
    )
  }

  return (
    <div
      className={`group relative flex items-center gap-2 rounded px-3 py-1.5 ${
        isActive ? 'bg-muted' : 'hover:bg-muted/50'
      }`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        {/* The session's mark (RFC 0311): the same one it draws everywhere, so a
            row is recognised before its title is read. */}
        <SessionMark
          id={session.id}
          size={14}
          badge={rowDot ? <StatusDot status={rowDot} /> : undefined}
        />
        {/* Leads the title, so scanning the list left-to-right answers "what is
            running" before you read a single name. A queued run gets the same
            mark dimmed: something is pending, but nothing has picked it up. */}
        {(session.status === 'running' || session.status === 'queued') && (
          <span className={session.status === 'queued' ? 'opacity-40' : ''}>
            <ActivityGrid
              title={
                session.status === 'queued'
                  ? `${session.title} is queued, no worker has taken it`
                  : `${session.title} is working`
              }
            />
          </span>
        )}
        <span
          className={`truncate text-sm ${
            isActive ? 'text-foreground' : 'text-foreground/90'
          }`}
        >
          {session.title}
        </span>
        {/* Trails the title and never truncates it: which project a row is from
            is context, and a name that pushed the session title out of view
            would be answering a question nobody asked first. */}
        {session.projectName && (
          <span
            className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
            title={`In ${session.projectName}`}
          >
            {session.projectName}
          </span>
        )}
      </button>

      {confirmingDelete ? (
        <span className="flex items-center gap-1">
          <button
            type="button"
            title="Confirm delete"
            onClick={() => {
              onDelete?.(session.id)
              setConfirmingDelete(false)
            }}
            className="rounded p-0.5 text-red-400 hover:bg-muted"
          >
            <Check size={13} />
          </button>
          <button
            type="button"
            title="Cancel"
            onClick={() => setConfirmingDelete(false)}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted"
          >
            <X size={13} />
          </button>
        </span>
      ) : (
        (onRename || onDelete || onComplete) && (
          <div className="relative">
            <button
              type="button"
              title="Session actions"
              onClick={() => setMenuOpen((o) => !o)}
              className={`rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground ${
                menuOpen ? '' : 'opacity-0 group-hover:opacity-100'
              }`}
            >
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <>
                {/* click-away */}
                <button
                  type="button"
                  aria-label="Close menu"
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 z-20 mt-1 w-32 overflow-hidden rounded-md border border-border bg-background py-1 shadow-lg">
                  {onRename && (
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(session.title)
                        setRenaming(true)
                        setMenuOpen(false)
                      }}
                      className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                    >
                      Rename
                    </button>
                  )}
                  {onComplete && (
                    <button
                      type="button"
                      onClick={() => {
                        onComplete(session.id, !done)
                        setMenuOpen(false)
                      }}
                      className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
                    >
                      {done ? 'Reopen' : 'Mark done'}
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmingDelete(true)
                        setMenuOpen(false)
                      }}
                      className="block w-full px-3 py-1.5 text-left text-red-400 text-sm hover:bg-muted"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )
      )}
    </div>
  )
}
